import { z } from 'zod';

/**
 * Caption styles: how a slide's text is drawn on its image (Figma "Caption
 * styles", 394:2). One definition and one drawing function, shared by the
 * API's slide renderer (@napi-rs/canvas) and the web app's live preview (a
 * browser canvas) - both speak the Canvas 2D API, so what you preview is
 * what gets rendered.
 */

/** Fonts a style can use. Files are served from the API's assets/fonts/caption. */
export const CAPTION_FONTS = [
  {
    id: 'tiktok-sans-bold',
    label: 'TikTok Sans Bold',
    family: 'TikTok Sans',
    weight: 700,
    style: 'normal',
    file: 'TikTokSans-Bold.woff',
  },
  {
    id: 'tiktok-sans-black',
    label: 'TikTok Sans Black',
    family: 'TikTok Sans',
    weight: 900,
    style: 'normal',
    file: 'TikTokSans-Black.woff',
  },
  {
    id: 'inter-semibold',
    label: 'Inter SemiBold',
    family: 'Inter',
    weight: 600,
    style: 'normal',
    file: 'Inter-SemiBold.woff',
  },
  {
    id: 'montserrat-bold',
    label: 'Montserrat Bold',
    family: 'Montserrat',
    weight: 700,
    style: 'normal',
    file: 'Montserrat-Bold.woff',
  },
  {
    id: 'anton',
    label: 'Anton',
    family: 'Anton',
    weight: 400,
    style: 'normal',
    file: 'Anton-Regular.woff',
  },
  {
    id: 'special-elite',
    label: 'Special Elite',
    family: 'Special Elite',
    weight: 400,
    style: 'normal',
    file: 'SpecialElite-Regular.woff',
  },
  {
    id: 'courier-prime-bold',
    label: 'Courier Prime Bold',
    family: 'Courier Prime',
    weight: 700,
    style: 'normal',
    file: 'CourierPrime-Bold.woff',
  },
  {
    id: 'pacifico',
    label: 'Pacifico',
    family: 'Pacifico',
    weight: 400,
    style: 'normal',
    file: 'Pacifico-Regular.woff',
  },
  {
    id: 'yellowtail',
    label: 'Yellowtail',
    family: 'Yellowtail',
    weight: 400,
    style: 'normal',
    file: 'Yellowtail-Regular.woff',
  },
  {
    id: 'playfair-italic',
    label: 'Playfair Display Italic',
    family: 'Playfair Display',
    weight: 400,
    style: 'italic',
    file: 'PlayfairDisplay-Italic.woff',
  },
  {
    id: 'playfair-bold-italic',
    label: 'Playfair Display Bold Italic',
    family: 'Playfair Display',
    weight: 700,
    style: 'italic',
    file: 'PlayfairDisplay-BoldItalic.woff',
  },
  {
    id: 'limelight',
    label: 'Limelight',
    family: 'Limelight',
    weight: 400,
    style: 'normal',
    file: 'Limelight-Regular.woff',
  },
] as const;
export type CaptionFont = (typeof CAPTION_FONTS)[number];
export type CaptionFontId = CaptionFont['id'];
const FONT_IDS = CAPTION_FONTS.map((f) => f.id) as [CaptionFontId, ...CaptionFontId[]];
export const captionFont = (id: CaptionFontId): CaptionFont =>
  CAPTION_FONTS.find((f) => f.id === id) ?? CAPTION_FONTS[0];

/** Colour emoji, registered by the renderer next to the caption fonts. */
export const EMOJI_FAMILY = 'Twemoji Mozilla';

const hex = z.string().regex(/^#[0-9a-fA-F]{6}$/, { error: 'Use a colour like #1A2B3C' });

/**
 * A caption style. Sizes are relative so a style reads the same at any image
 * size: `size` is a percentage of the image width, `width` a fraction of it,
 * and spacing, padding, radius and outline are in ems of the text size.
 */
export const captionStyleSchema = z.object({
  font: z.enum(FONT_IDS),
  size: z.number().min(3).max(12),
  case: z.enum(['none', 'upper', 'lower']).default('none'),
  letterSpacing: z.number().min(-0.1).max(0.5).default(0),
  lineHeight: z.number().min(0.9).max(2).default(1.2),
  color: hex,
  background: z.object({
    /** Behind each line (TikTok), one box behind the whole block, or nothing. */
    shape: z.enum(['none', 'line', 'block']),
    color: hex,
    opacity: z.number().min(0).max(1),
    radius: z.number().min(0).max(1),
    padding: z.number().min(0).max(1.5),
  }),
  /** Width 0 is no outline. */
  outline: z.object({ width: z.number().min(0).max(0.4), color: hex }),
  shadow: z.object({
    kind: z.enum(['none', 'soft', 'hard', 'glow']),
    color: hex,
    opacity: z.number().min(0).max(1),
  }),
  align: z.enum(['left', 'center', 'right']).default('center'),
  width: z.number().min(0.4).max(0.95).default(0.86),
  /** Words wrapped in *asterisks* in a caption get this colour. */
  highlight: z.object({ enabled: z.boolean(), color: hex }),
});
export type CaptionStyle = z.output<typeof captionStyleSchema>;
export type CaptionPosition = 'top' | 'center' | 'bottom';

const none = {
  shape: 'none',
  color: '#000000',
  opacity: 1,
  radius: 0,
  padding: 0,
} as const;
const noOutline = { width: 0, color: '#000000' };
const noShadow = { kind: 'none', color: '#000000', opacity: 0 } as const;
const noHighlight = { enabled: false, color: '#FFE14D' };
const base = {
  case: 'none',
  letterSpacing: 0,
  lineHeight: 1.2,
  align: 'center',
  width: 0.86,
  outline: noOutline,
  shadow: noShadow,
  highlight: noHighlight,
} as const;

export type CaptionPreset = {
  id: string;
  name: string;
  source: 'tiktok' | 'instagram';
  style: CaptionStyle;
};

/** Ready-made looks, inspired by TikTok's and Instagram's text styles. */
export const CAPTION_PRESETS: CaptionPreset[] = [
  // The Keeps slides-creator's TikTok look: 5.5% text on bands padded 0.38em with 0.34em corners.
  {
    id: 'tiktok-white-band',
    name: 'White band',
    source: 'tiktok',
    style: {
      ...base,
      font: 'tiktok-sans-bold',
      size: 5.5,
      color: '#000000',
      background: {
        shape: 'line',
        color: '#FFFFFF',
        opacity: 1,
        radius: 0.34,
        padding: 0.38,
      },
    },
  },
  {
    id: 'tiktok-black-band',
    name: 'Black band',
    source: 'tiktok',
    style: {
      ...base,
      font: 'tiktok-sans-bold',
      size: 5.5,
      color: '#FFFFFF',
      background: {
        shape: 'line',
        color: '#000000',
        opacity: 1,
        radius: 0.34,
        padding: 0.38,
      },
    },
  },
  {
    id: 'tiktok-soft-band',
    name: 'Soft band',
    source: 'tiktok',
    style: {
      ...base,
      font: 'tiktok-sans-bold',
      size: 5.5,
      color: '#FFFFFF',
      background: {
        shape: 'line',
        color: '#000000',
        opacity: 0.55,
        radius: 0.34,
        padding: 0.38,
      },
    },
  },
  {
    id: 'tiktok-classic',
    name: 'Classic',
    source: 'tiktok',
    style: {
      ...base,
      font: 'tiktok-sans-bold',
      size: 5.8,
      color: '#FFFFFF',
      background: none,
      shadow: { kind: 'soft', color: '#000000', opacity: 0.6 },
    },
  },
  {
    id: 'tiktok-outlined',
    name: 'Outlined',
    source: 'tiktok',
    style: {
      ...base,
      font: 'tiktok-sans-bold',
      size: 5.5,
      color: '#FFFFFF',
      background: none,
      outline: { width: 0.14, color: '#000000' },
    },
  },
  {
    id: 'tiktok-neon',
    name: 'Neon',
    source: 'tiktok',
    style: {
      ...base,
      font: 'pacifico',
      size: 5.8,
      lineHeight: 1.35,
      color: '#FFFFFF',
      background: none,
      shadow: { kind: 'glow', color: '#FF3D9A', opacity: 0.95 },
    },
  },
  {
    id: 'tiktok-typewriter',
    name: 'Typewriter',
    source: 'tiktok',
    style: {
      ...base,
      font: 'courier-prime-bold',
      size: 5.2,
      color: '#FFFFFF',
      background: none,
      shadow: { kind: 'hard', color: '#000000', opacity: 0.7 },
    },
  },
  {
    id: 'instagram-classic',
    name: 'Classic',
    source: 'instagram',
    style: {
      ...base,
      font: 'inter-semibold',
      size: 5.2,
      width: 0.8,
      color: '#111111',
      background: {
        shape: 'block',
        color: '#FFFFFF',
        opacity: 1,
        radius: 0.25,
        padding: 0.5,
      },
    },
  },
  {
    id: 'instagram-modern',
    name: 'Modern',
    source: 'instagram',
    style: {
      ...base,
      font: 'montserrat-bold',
      size: 4.6,
      case: 'upper',
      letterSpacing: 0.08,
      color: '#FFFFFF',
      background: none,
      shadow: { kind: 'soft', color: '#000000', opacity: 0.4 },
    },
  },
  {
    id: 'instagram-strong',
    name: 'Strong',
    source: 'instagram',
    style: {
      ...base,
      font: 'anton',
      size: 7,
      case: 'upper',
      lineHeight: 1.05,
      width: 0.78,
      color: '#FFFFFF',
      background: {
        shape: 'block',
        color: '#FF4D2E',
        opacity: 1,
        radius: 0,
        padding: 0.35,
      },
    },
  },
  {
    id: 'instagram-typewriter',
    name: 'Typewriter',
    source: 'instagram',
    style: {
      ...base,
      font: 'special-elite',
      size: 5,
      color: '#111111',
      background: {
        shape: 'line',
        color: '#FFF8E6',
        opacity: 1,
        radius: 0.12,
        padding: 0.3,
      },
    },
  },
  {
    id: 'instagram-neon',
    name: 'Neon',
    source: 'instagram',
    style: {
      ...base,
      font: 'yellowtail',
      size: 7,
      lineHeight: 1.1,
      color: '#FFFBE8',
      background: none,
      shadow: { kind: 'glow', color: '#3DE2FF', opacity: 0.95 },
    },
  },
  {
    id: 'instagram-literature',
    name: 'Literature',
    source: 'instagram',
    style: {
      ...base,
      font: 'playfair-italic',
      size: 6,
      color: '#FFFFFF',
      background: none,
      shadow: { kind: 'soft', color: '#000000', opacity: 0.45 },
    },
  },
  {
    id: 'instagram-deco',
    name: 'Deco',
    source: 'instagram',
    style: {
      ...base,
      font: 'limelight',
      size: 5.4,
      color: '#F5D77A',
      background: none,
      shadow: { kind: 'hard', color: '#000000', opacity: 0.55 },
    },
  },
];

export const DEFAULT_CAPTION_STYLE = 'tiktok-white-band';

export const presetById = (id: string): CaptionPreset | undefined =>
  CAPTION_PRESETS.find((p) => p.id === id);

/** The three looks before presets, as the presets that replaced them. */
export const LEGACY_CAPTION_STYLES: Record<string, string> = {
  tiktokWhite: 'tiktok-white-band',
  tiktokBlack: 'tiktok-black-band',
  outlined: 'tiktok-outlined',
};

/** A style picked for a carousel or slide: a preset id or a custom style's id. Old names still parse. */
export const captionStyleRefSchema = z.preprocess(
  (value) =>
    typeof value === 'string' ? (LEGACY_CAPTION_STYLES[value] ?? value) : value,
  z.string().min(1).max(64),
);

/* ------------------------------------------------------------ highlights */

export type CaptionWord = { text: string; highlight: boolean };

/**
 * A caption's words, with the ones inside *asterisks* marked. The asterisks
 * never show; an unclosed one is kept as text.
 */
export function parseHighlights(text: string): CaptionWord[][] {
  return text.split('\n').map((paragraph) => {
    const words: CaptionWord[] = [];
    // Like Markdown emphasis: no space just inside the asterisks, so "5* stay" stays as it is.
    const parts = paragraph.split(/(\*[^*\s](?:[^*\n]*[^*\s])?\*)/);
    for (const part of parts) {
      const marked = part.length > 2 && part.startsWith('*') && part.endsWith('*');
      const inner = marked ? part.slice(1, -1) : part;
      for (const word of inner.split(/\s+/).filter(Boolean))
        words.push({ text: word, highlight: marked });
    }
    return words;
  });
}

/** A caption with its highlight marks removed. */
export const plainCaption = (text: string) =>
  parseHighlights(text)
    .map((line) => line.map((w) => w.text).join(' '))
    .join('\n');

/* --------------------------------------------------------------- drawing */

/** The part of CanvasRenderingContext2D (browser) / SKRSContext2D (@napi-rs/canvas) the drawing uses. */
export type CaptionCanvas = {
  font: string;
  fillStyle: unknown;
  strokeStyle: unknown;
  lineWidth: number;
  lineJoin: string;
  textAlign: string;
  textBaseline: string;
  globalAlpha: number;
  shadowColor: string;
  shadowBlur: number;
  shadowOffsetX: number;
  shadowOffsetY: number;
  letterSpacing?: string;
  measureText(text: string): { width: number };
  fillText(text: string, x: number, y: number): void;
  /** Only needed when emoji are drawn as images. */
  drawImage?(image: never, x: number, y: number, w: number, h: number): void;
  strokeText(text: string, x: number, y: number): void;
  fillRect(x: number, y: number, w: number, h: number): void;
  beginPath(): void;
  roundRect(x: number, y: number, w: number, h: number, r: number): void;
  fill(): void;
  save(): void;
  restore(): void;
};

/** Where the text block's middle sits, as a fraction of the image height. */
export const CAPTION_CENTER_Y: Record<CaptionPosition, number> = {
  top: 0.22,
  center: 0.5,
  bottom: 0.78,
};

const rgba = (color: string, alpha: number) => {
  const n = parseInt(color.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

export const captionFontCss = (style: CaptionStyle, px: number) => {
  const f = captionFont(style.font);
  return `${f.style} ${f.weight} ${px}px "${f.family}", "${EMOJI_FAMILY}", sans-serif`;
};

type Line = { words: CaptionWord[]; width: number };

/* ----------------------------------------------------------------- emoji */

/** A stretch of a word: plain text, or one emoji. */
export type CaptionRun = { text: string } | { emoji: string };

/**
 * Graphemes that show as emoji, as on an iPhone: pictographs (a bare ❤ too),
 * flags and keycaps - but not ©, ®, ™ or arrows, nor anything asking for text style.
 */
const EMOJI = /\p{Extended_Pictographic}|\p{Regional_Indicator}|\u20E3/u;
const TEXT_STYLE = /^[\u00A9\u00AE\u2122\u2190-\u21FF]$|\uFE0E/u;
const graphemes = new Intl.Segmenter('en', { granularity: 'grapheme' });

/** Text split into its emoji (one per run, ZWJ sequences, skin tones and flags kept whole) and the text between. */
export function splitEmoji(text: string): CaptionRun[] {
  const runs: CaptionRun[] = [];
  for (const { segment } of graphemes.segment(text)) {
    const last = runs.at(-1);
    if (EMOJI.test(segment) && !TEXT_STYLE.test(segment)) runs.push({ emoji: segment });
    else if (last && 'text' in last) last.text += segment;
    else runs.push({ text: segment });
  }
  return runs;
}

/** An emoji drawn as an image: its square's side, and how far it moves the pen, in font sizes. */
const EMOJI_SIZE = 1.1;
const EMOJI_ADVANCE = 1.18;

/** A word ready to draw: text runs, and emoji that have an image. */
type Piece = { text: string } | { image: unknown };

/** Greedy wrap by measured width; line breaks in the text are kept. */
function wrap(
  paragraphs: CaptionWord[][],
  maxWidth: number,
  measure: (s: string) => number,
): Line[] {
  const lines: Line[] = [];
  for (const words of paragraphs) {
    let line: CaptionWord[] = [];
    for (const word of words) {
      const next = [...line, word];
      if (line.length && measure(next.map((w) => w.text).join(' ')) > maxWidth) {
        lines.push({ words: line, width: measure(line.map((w) => w.text).join(' ')) });
        line = [word];
      } else line = next;
    }
    if (line.length)
      lines.push({ words: line, width: measure(line.map((w) => w.text).join(' ')) });
  }
  return lines;
}

/**
 * Draws a caption onto a canvas that already holds the slide's image. Empty
 * text draws nothing. Returns the lines drawn (for tests).
 */
export function drawCaption(
  ctx: CaptionCanvas,
  spec: {
    width: number;
    height: number;
    text: string;
    position: CaptionPosition;
    style: CaptionStyle;
    /**
     * An image for an emoji, to draw it as a picture rather than with the
     * emoji font (e.g. Apple's emoji on a server). Undefined keeps the font.
     */
    emoji?: (emoji: string) => unknown;
  },
): string[] {
  const { width, height, style } = spec;
  const cased =
    style.case === 'upper'
      ? spec.text.toUpperCase()
      : style.case === 'lower'
        ? spec.text.toLowerCase()
        : spec.text;
  const paragraphs = parseHighlights(cased.trim());
  if (!paragraphs.some((p) => p.length)) return [];

  const fontSize = Math.round((width * style.size) / 100);
  const lineHeight = fontSize * style.lineHeight;
  const pad = fontSize * style.background.padding;
  const radius = fontSize * style.background.radius;

  ctx.save();
  ctx.font = captionFontCss(style, fontSize);
  if ('letterSpacing' in ctx) ctx.letterSpacing = `${fontSize * style.letterSpacing}px`;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';

  // Emoji with an image are drawn as pictures; the rest of the text as text.
  const emojiSize = fontSize * EMOJI_SIZE;
  const piecesOf = (s: string): Piece[] =>
    spec.emoji
      ? splitEmoji(s).reduce<Piece[]>((pieces, run) => {
          const image = 'emoji' in run ? spec.emoji!(run.emoji) : undefined;
          const text = 'text' in run ? run.text : run.emoji;
          const last = pieces.at(-1);
          if (image) pieces.push({ image });
          else if (last && 'text' in last) last.text += text;
          else pieces.push({ text });
          return pieces;
        }, [])
      : [{ text: s }];
  const measure = (s: string) =>
    piecesOf(s).reduce(
      (w, piece) =>
        w +
        ('text' in piece ? ctx.measureText(piece.text).width : fontSize * EMOJI_ADVANCE),
      0,
    );

  const blockPad = style.background.shape === 'block' ? pad : 0;
  const lines = wrap(paragraphs, width * style.width - blockPad * 2, measure);
  const blockHeight = lines.length * lineHeight;
  const top = height * CAPTION_CENTER_Y[spec.position] - blockHeight / 2;
  const margin = (width * (1 - style.width)) / 2;
  const widest = Math.max(...lines.map((l) => l.width));
  // Where a line of this width starts, for the chosen alignment.
  const startX = (w: number) =>
    style.align === 'left'
      ? margin + blockPad
      : style.align === 'right'
        ? width - margin - blockPad - w
        : (width - w) / 2;

  // Background
  if (style.background.shape !== 'none' && style.background.opacity > 0) {
    ctx.globalAlpha = style.background.opacity;
    ctx.fillStyle = style.background.color;
    if (style.background.shape === 'block') {
      const x =
        style.align === 'left'
          ? margin
          : style.align === 'right'
            ? width - margin - widest - pad * 2
            : (width - widest) / 2 - pad;
      ctx.beginPath();
      ctx.roundRect(x, top - pad, widest + pad * 2, blockHeight + pad * 2, radius);
      ctx.fill();
    } else {
      // A band per line; where two meet, the inner corners are squared off so the block reads as one shape.
      const bands = lines.map((l) => ({
        x: startX(l.width) - pad,
        w: l.width + pad * 2,
      }));
      bands.forEach((band, i) => {
        ctx.beginPath();
        ctx.roundRect(band.x, top + i * lineHeight, band.w, lineHeight, radius);
        ctx.fill();
        const next = bands[i + 1];
        if (next) {
          const left = Math.max(band.x, next.x);
          const right = Math.min(band.x + band.w, next.x + next.w);
          if (right > left)
            ctx.fillRect(
              left,
              top + (i + 1) * lineHeight - radius,
              right - left,
              radius * 2,
            );
        }
      });
    }
    ctx.globalAlpha = 1;
  }

  // Text: outline first, then the fill with its shadow.
  const space = ctx.measureText(' ').width;
  const eachWord = (draw: (word: CaptionWord, x: number, y: number) => void) =>
    lines.forEach((line, i) => {
      let x = startX(line.width);
      const y = top + i * lineHeight + lineHeight / 2;
      for (const word of line.words) {
        draw(word, x, y);
        x += measure(word.text) + space;
      }
    });
  // Each piece of a word with where it starts.
  const eachPiece = (
    word: CaptionWord,
    x: number,
    draw: (piece: Piece, x: number) => void,
  ) => {
    for (const piece of piecesOf(word.text)) {
      draw(piece, x);
      x += 'text' in piece ? ctx.measureText(piece.text).width : fontSize * EMOJI_ADVANCE;
    }
  };

  if (style.outline.width > 0) {
    ctx.lineJoin = 'round';
    ctx.lineWidth = fontSize * style.outline.width;
    ctx.strokeStyle = style.outline.color;
    eachWord((word, x, y) =>
      eachPiece(word, x, (piece, px) => {
        if ('text' in piece) ctx.strokeText(piece.text, px, y);
      }),
    );
  }

  const shadow = style.shadow;
  const passes =
    shadow.kind === 'none'
      ? [null]
      : shadow.kind === 'glow'
        ? [
            { blur: 0.9, y: 0 },
            { blur: 0.35, y: 0 },
          ]
        : shadow.kind === 'hard'
          ? [{ blur: 0.08, y: 0.08 }]
          : [{ blur: 0.35, y: 0.06 }];
  for (const pass of passes) {
    ctx.shadowColor = pass ? rgba(shadow.color, shadow.opacity) : 'transparent';
    ctx.shadowBlur = pass ? fontSize * pass.blur : 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = pass ? fontSize * pass.y : 0;
    eachWord((word, x, y) => {
      ctx.fillStyle =
        word.highlight && style.highlight.enabled ? style.highlight.color : style.color;
      eachPiece(word, x, (piece, px) => {
        if ('text' in piece) ctx.fillText(piece.text, px, y);
      });
    });
  }

  // Emoji pictures last, once, without the text's shadow.
  if (spec.emoji) {
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;
    const gap = (fontSize * EMOJI_ADVANCE - emojiSize) / 2;
    eachWord((word, x, y) =>
      eachPiece(word, x, (piece, px) => {
        if ('image' in piece)
          ctx.drawImage?.(
            piece.image as never,
            px + gap,
            y - emojiSize / 2,
            emojiSize,
            emojiSize,
          );
      }),
    );
  }
  ctx.restore();
  return lines.map((l) => l.words.map((w) => w.text).join(' '));
}
